import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, AlertCircle, CheckCircle, XCircle, AlertTriangle, RefreshCw, Download } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { enhancementsApi } from '@/services/api/enhancements'
import { IssueCard } from './IssueCard'
import { FeedbackDialog } from './FeedbackDialog'
import type { EvaluationIssue, FeedbackType } from '@/types/api'

// Map dialog reason to FeedbackType for API submission
const REASON_TO_FEEDBACK_TYPE: Record<string, FeedbackType> = {
  not_an_error: 'not_an_error',
  detection_error: 'detection_error',
  other: 'other',
}

interface IssueViewDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  displayNumber: string
  articleTitle: string
  showVerification?: boolean  // Show verification results (for enhancement results)
  onRequestFreshEvaluation?: (displayNumber: string) => void
}

export function IssueViewDialog({
  open,
  onOpenChange,
  displayNumber,
  articleTitle,
  showVerification = false,
  onRequestFreshEvaluation,
}: IssueViewDialogProps) {
  const queryClient = useQueryClient()
  const [feedbackIssue, setFeedbackIssue] = useState<EvaluationIssue | null>(null)
  const [isExporting, setIsExporting] = useState(false)

  // Fetch issues
  const { data, isLoading, error } = useQuery({
    queryKey: ['article-issues', displayNumber],
    queryFn: () => enhancementsApi.getArticleIssues(displayNumber),
    enabled: open && !!displayNumber,
  })

  // Submit feedback mutation
  const feedbackMutation = useMutation({
    mutationFn: enhancementsApi.submitFeedback,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['article-issues', displayNumber] })
      setFeedbackIssue(null)
    },
  })

  const handleReportError = (issueId: string) => {
    const issue = data?.issues.find(i => i.issue_id === issueId)
    if (issue) setFeedbackIssue(issue)
  }

  const handleFeedbackSubmit = (reason: string, comment: string) => {
    if (!feedbackIssue) return

    const feedbackType = REASON_TO_FEEDBACK_TYPE[reason] || 'other'

    feedbackMutation.mutate({
      display_number: displayNumber,
      issue_id: feedbackIssue.issue_id,
      feedback_type: feedbackType,
      feedback_reason: reason,
      feedback_comment: comment,
    })
  }

  const handleExport = async () => {
    setIsExporting(true)
    try {
      await enhancementsApi.exportArticleIssues(displayNumber)
    } catch (err) {
      console.error('Failed to export issues:', err)
    } finally {
      setIsExporting(false)
    }
  }

  // Build verification map
  const verificationMap = new Map(
    data?.verification_results?.map(v => [v.issue_id, v]) || []
  )

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="pr-8">
              Issues for {articleTitle || displayNumber}
            </DialogTitle>
          </DialogHeader>

          {/* Summary */}
          {data?.summary && (
            <div className="flex flex-wrap items-center gap-4 py-2 px-3 bg-gray-50 rounded-lg text-sm">
              <span>Total: <strong>{data.summary.total}</strong></span>
              <span className="text-red-600 flex items-center gap-1">
                <XCircle className="h-3.5 w-3.5" />
                High: {data.summary.high}
              </span>
              <span className="text-yellow-600 flex items-center gap-1">
                <AlertTriangle className="h-3.5 w-3.5" />
                Medium: {data.summary.medium}
              </span>
              <span className="text-blue-600 flex items-center gap-1">
                <AlertCircle className="h-3.5 w-3.5" />
                Low: {data.summary.low}
              </span>
              {showVerification && data.summary.resolved !== undefined && (
                <>
                  <span className="border-l pl-4 text-green-600 flex items-center gap-1">
                    <CheckCircle className="h-3.5 w-3.5" />
                    Resolved: {data.summary.resolved}
                  </span>
                  {data.summary.unresolved !== undefined && data.summary.unresolved > 0 && (
                    <span className="text-red-600">
                      Unresolved: {data.summary.unresolved}
                    </span>
                  )}
                  {data.summary.manual_required !== undefined && data.summary.manual_required > 0 && (
                    <span className="text-orange-600">
                      Manual: {data.summary.manual_required}
                    </span>
                  )}
                </>
              )}
            </div>
          )}

          {/* Comparison mode banner */}
          {showVerification && data?.is_comparison_mode && data.summary && (
            <div className="flex items-center gap-2 py-2 px-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
              <RefreshCw className="h-4 w-4 shrink-0" />
              <span>
                <strong>Comparison Mode:</strong>{' '}
                {data.summary.resolved ?? 0} of {data.summary.total} issues resolved from previous enhancement.
                {(data.summary.manual_required ?? 0) > 0 && ` ${data.summary.manual_required} require manual action.`}
              </span>
            </div>
          )}

          {/* Issues list */}
          <div className="flex-1 overflow-y-auto space-y-3 pr-2 mt-2">
            {isLoading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                <span className="ml-2 text-gray-500">Loading issues...</span>
              </div>
            )}

            {error && (
              <div className="flex items-center gap-2 text-red-600 py-4 px-3 bg-red-50 rounded-lg">
                <AlertCircle className="h-5 w-5" />
                <span>Failed to load issues. Please try again.</span>
              </div>
            )}

            {data?.issues.map(issue => (
              <IssueCard
                key={issue.issue_id}
                issue={issue}
                verification={showVerification ? verificationMap.get(issue.issue_id) : undefined}
                onReportError={handleReportError}
                disabled={feedbackMutation.isPending}
              />
            ))}

            {data?.issues.length === 0 && !isLoading && (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-500" />
                <p className="font-medium">No issues found for this article</p>
                <p className="text-sm mt-1">This article passed all quality checks.</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-between pt-4 border-t">
            <div>
              {showVerification && data?.is_comparison_mode && onRequestFreshEvaluation && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onRequestFreshEvaluation(displayNumber)}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Request Fresh Evaluation
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                disabled={isExporting || !data?.issues?.length}
              >
                {isExporting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Export
              </Button>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Feedback reason dialog */}
      <FeedbackDialog
        open={!!feedbackIssue}
        onOpenChange={(open) => !open && setFeedbackIssue(null)}
        issue={feedbackIssue}
        onSubmit={handleFeedbackSubmit}
        isLoading={feedbackMutation.isPending}
      />
    </>
  )
}
